import * as React from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send, RefreshCw, Inbox as InboxIcon, ArrowLeft } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/ui/field';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { env } from '@/lib/env';
import { cn } from '@/lib/cn';

/**
 * Operator inbox — Cloudflare-native mailbox for max@pozyskajpacjenta.pl
 * and (eventually) agent@. Split-pane email-client layout inspired by
 * cloudflare/agentic-inbox: conversation list on the left, selected
 * thread + reply composer on the right.
 *
 * Reuses the existing /api/operator/conversations endpoints — same data
 * the AI-intake page shows, but framed as a plain email client without
 * the state machine chrome.
 *
 * Auth: operator-only (the mailbox is the operator's). Per-clinic
 * inboxes will live under /messages with tenant-scoped routes when we
 * add per-tenant email later.
 */
export const Route = createFileRoute('/_app/inbox')({
  staticData: { crumb: 'Inbox' },
  beforeLoad: ({ context }) => {
    const ctx = context as { session: { role: string } };
    if (ctx.session.role !== 'operator') throw redirect({ to: '/' });
  },
  component: InboxPage,
});

interface ConvRow {
  id: string;
  contact_email: string;
  tenant_legal_name: string | null;
  state: string;
  last_message_at: number | null;
  created_at: number;
}

interface MessageRow {
  id: string;
  conversation_id: string;
  direction: 'inbound' | 'outbound';
  from_addr: string;
  to_addr: string;
  subject: string;
  body_text: string | null;
  body_html: string | null;
  ts: number;
}

interface ConvDetail {
  conversation: ConvRow & {
    escalation_reason: string | null;
    last_preview_url: string | null;
    tenant_id: string | null;
    tenant_slug: string | null;
  };
  messages: MessageRow[];
}

function InboxPage() {
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['inbox', 'conversations'],
    queryFn: async (): Promise<{ conversations: ConvRow[] }> => {
      const res = await fetch(`${env.apiBaseUrl}/api/operator/conversations`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('load_failed');
      return res.json();
    },
    refetchInterval: 30_000,
  });

  return (
    <div className="-mx-6 -my-6 flex h-[calc(100vh-4rem)] flex-col md:-mx-8">
      <header className="flex items-center justify-between border-b border-border bg-bg px-6 py-3 md:px-8">
        <div className="flex items-center gap-3">
          <InboxIcon className="size-5 text-text-muted" aria-hidden="true" />
          <h1 className="font-display text-lg font-semibold tracking-tight text-text">
            Inbox
          </h1>
          <span className="text-xs text-text-subtle">
            {list.data ? `${list.data.conversations.length} wątków` : ''}
          </span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => qc.invalidateQueries({ queryKey: ['inbox'] })}
          disabled={list.isFetching}
        >
          <RefreshCw className={cn('size-3.5', list.isFetching && 'animate-spin')} aria-hidden="true" />
          Odśwież
        </Button>
      </header>

      <div className="grid flex-1 grid-cols-1 overflow-hidden md:grid-cols-[20rem_minmax(0,1fr)]">
        {/* CONVERSATION LIST */}
        <aside
          className={cn(
            'overflow-y-auto border-r border-border bg-surface/40',
            selectedId && 'hidden md:block'
          )}
        >
          {list.isPending ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : (list.data?.conversations ?? []).length === 0 ? (
            <EmptyState />
          ) : (
            <ul role="list" className="divide-y divide-border">
              {list.data!.conversations.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(c.id)}
                    className={cn(
                      'block w-full px-4 py-3 text-left transition-colors hover:bg-bg',
                      selectedId === c.id && 'bg-bg shadow-[inset_3px_0_0_var(--accent)]'
                    )}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="truncate text-sm font-medium text-text">
                        {c.tenant_legal_name ?? c.contact_email}
                      </p>
                      <p className="shrink-0 text-[10px] text-text-subtle">
                        {formatRelative(c.last_message_at ?? c.created_at)}
                      </p>
                    </div>
                    <p className="mt-0.5 truncate text-xs text-text-muted">{c.contact_email}</p>
                    <p className="mt-1 text-[10px] uppercase tracking-wide text-text-subtle">
                      {c.state}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* THREAD + COMPOSER */}
        <section className={cn('flex flex-col overflow-hidden', !selectedId && 'hidden md:flex')}>
          {selectedId ? (
            <ConversationView
              key={selectedId}
              id={selectedId}
              onBack={() => setSelectedId(null)}
            />
          ) : (
            <div className="flex flex-1 items-center justify-center bg-bg">
              <p className="text-sm text-text-muted">Wybierz wątek po lewej.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="grid h-full place-items-center p-8 text-center">
      <div className="max-w-xs">
        <InboxIcon className="mx-auto size-10 text-text-subtle" aria-hidden="true" />
        <p className="mt-4 font-display text-base font-semibold text-text">Skrzynka pusta</p>
        <p className="mt-1 text-xs text-text-muted">
          Wiadomości do <span className="font-mono">max@pozyskajpacjenta.pl</span> pojawią się tutaj.
          Wyślij testowy email, żeby sprawdzić integrację.
        </p>
      </div>
    </div>
  );
}

function ConversationView({ id, onBack }: { id: string; onBack: () => void }) {
  const qc = useQueryClient();

  const detail = useQuery({
    queryKey: ['inbox', 'conversation', id],
    queryFn: async (): Promise<ConvDetail> => {
      const res = await fetch(
        `${env.apiBaseUrl}/api/operator/conversations/${encodeURIComponent(id)}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('load_failed');
      return res.json();
    },
    refetchInterval: 30_000,
  });

  const [subject, setSubject] = React.useState('');
  const [bodyText, setBodyText] = React.useState('');
  const subjectInitRef = React.useRef<string | null>(null);

  // Pre-fill subject with Re: <last inbound subject> on first load.
  React.useEffect(() => {
    if (subjectInitRef.current === id) return;
    const lastInbound = (detail.data?.messages ?? [])
      .slice()
      .reverse()
      .find((m) => m.direction === 'inbound');
    if (lastInbound) {
      const s = lastInbound.subject.startsWith('Re: ')
        ? lastInbound.subject
        : `Re: ${lastInbound.subject}`;
      setSubject(s);
      subjectInitRef.current = id;
    }
  }, [detail.data, id]);

  const reply = useMutation({
    mutationFn: async (data: { subject: string; body_text: string }) => {
      const res = await fetch(
        `${env.apiBaseUrl}/api/operator/conversations/${encodeURIComponent(id)}/reply`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(data),
        }
      );
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'reply_failed');
      }
      return res.json();
    },
    onSuccess: () => {
      setBodyText('');
      qc.invalidateQueries({ queryKey: ['inbox', 'conversation', id] });
      qc.invalidateQueries({ queryKey: ['inbox', 'conversations'] });
      toast.success('Wiadomość wysłana');
    },
    onError: () => {
      toast.error(
        'Wysyłka nie powiodła się. Sprawdź czy domena pozyskajpacjenta.pl jest onboardowana w Email Sending.'
      );
    },
  });

  if (detail.isPending) {
    return (
      <div className="space-y-3 p-6">
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <p className="p-6 text-sm text-error">
        Nie udało się załadować rozmowy.
      </p>
    );
  }

  const conv = detail.data.conversation;
  const messages = detail.data.messages;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Subject + sender header */}
      <header className="border-b border-border bg-bg px-6 py-3 md:px-8">
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" size="sm" onClick={onBack} className="md:hidden">
            <ArrowLeft className="size-3.5" aria-hidden="true" /> Lista
          </Button>
          <div className="min-w-0 flex-1">
            <p className="truncate font-display text-base font-semibold text-text">
              {messages[0]?.subject ?? '(brak tematu)'}
            </p>
            <p className="truncate text-xs text-text-muted">
              {conv.contact_email}
              {conv.tenant_legal_name ? ` · ${conv.tenant_legal_name}` : ''}
            </p>
          </div>
        </div>
      </header>

      {/* Message thread — scrollable */}
      <div className="flex-1 overflow-y-auto px-6 py-6 md:px-8">
        {messages.length === 0 ? (
          <p className="text-sm text-text-muted">Brak wiadomości.</p>
        ) : (
          <ul role="list" className="space-y-4">
            {messages.map((m) => (
              <MessageBubble key={m.id} m={m} />
            ))}
          </ul>
        )}
      </div>

      {/* Reply composer — sticky bottom */}
      <div className="border-t border-border bg-bg p-4 md:p-6">
        <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-start">
          <div className="space-y-2">
            <Field label="Temat" htmlFor="subj" hint="">
              <Input id="subj" value={subject} onChange={(e) => setSubject(e.target.value)} />
            </Field>
            <Field label="Wiadomość" htmlFor="body" hint="">
              <Textarea
                id="body"
                rows={4}
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                placeholder="Dzień dobry,&#10;&#10;…"
              />
            </Field>
          </div>
          <div className="flex md:flex-col md:items-stretch md:justify-end">
            <Button
              type="button"
              disabled={!subject || !bodyText || reply.isPending}
              onClick={() => reply.mutate({ subject, body_text: bodyText })}
              className="w-full"
            >
              <Send className="size-4" aria-hidden="true" />
              {reply.isPending ? 'Wysyłam…' : 'Wyślij'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ m }: { m: MessageRow }) {
  const isInbound = m.direction === 'inbound';
  const time = new Date(m.ts * 1000).toLocaleString('pl-PL', {
    timeZone: 'Europe/Warsaw',
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  return (
    <li>
      <Card
        className={cn(
          'overflow-hidden',
          isInbound ? 'bg-surface ring-1 ring-black/5' : 'bg-accent/5 ring-1 ring-accent/20'
        )}
      >
        <CardContent className="space-y-2 p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2 text-xs">
            <p className="font-medium text-text">
              {isInbound ? '← ' : '→ '}
              {m.from_addr}
            </p>
            <p className="text-text-subtle">{time}</p>
          </div>
          {m.body_text ? (
            <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-text">
              {m.body_text}
            </pre>
          ) : m.body_html ? (
            <div
              className="prose-sm max-w-none text-sm text-text"
              // The HTML came from postal-mime which we trust at parse time;
              // a future enhancement is sanitization with DOMPurify or
              // sandboxing in an iframe (agentic-inbox uses an EmailIframe).
              dangerouslySetInnerHTML={{ __html: m.body_html }}
            />
          ) : (
            <p className="text-xs text-text-subtle italic">(pusta wiadomość)</p>
          )}
        </CardContent>
      </Card>
    </li>
  );
}

/** "5 min ago" / "2h ago" / "yesterday" / "12 May" — short relative format. */
function formatRelative(ts: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = now - ts;
  if (diff < 60) return 'teraz';
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 2) return 'wczoraj';
  return new Date(ts * 1000).toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' });
}
